import { RootState, useAppDispatch, useAppSelector } from 'shared/store/store'
import { getSendEmailRecipients } from 'shared/store/sendMessage/email/selectors'
import { getSendMessageRecipients } from 'shared/store/sendMessage/message/selector'
import {
  addAllRecipients,
  addRecipient,
  addRecipientsToRemovalQueue,
  addRecipientToRemovalQueue,
  removeAllRecipients,
  removeRecipient,
  removeRecipientFromRemovalQueue,
  removeRecipientsFromRemovalQueue,
} from 'shared/store/sendMessage/recipients'
import {
  getMessageRecipients,
  getListedRecipients,
  getListedRecipientsRemovalQueue,
} from 'shared/store/sendMessage/recipients/selectors'
import { getSendSmsRecipients } from 'shared/store/sendMessage/sms/selectors'
import { IMessageRecipient } from '../models/Inbox'
import { IRecipientModel, RecipientOpenState } from '../models/Recipients'

export const blockNotLoggedInUsersForState = {
  Email: false,
  SMS: false,
  Message: true,
}

const getSelector = (state: RecipientOpenState) => {
  if (state === 'Email') return getSendEmailRecipients
  if (state === 'SMS') return getSendSmsRecipients

  return getSendMessageRecipients
}

const useRecipientsChecked = (state: RecipientOpenState) => {
  const dispatch = useAppDispatch()

  const recipients = useAppSelector(getMessageRecipients)

  const selector = getSelector(state)

  const initialRecipients = useAppSelector(
    selector as (state: RootState) => IMessageRecipient[]
  )

  const listedRecipients = useAppSelector(getListedRecipients)
  const listedRecipientsRemovalQueue = useAppSelector(
    getListedRecipientsRemovalQueue
  )

  const _checkListedRemovalQueue = (recipient: IRecipientModel) =>
    listedRecipientsRemovalQueue.find(
      (r) => r.nameId === recipient.nameId || r.email === recipient.email
    )

  const _checkInitialRecipients = (recipient: IRecipientModel) =>
    initialRecipients.some(
      (r) =>
        (r.nameregisterId === recipient.nameId ||
          (!r.nameregisterId &&
            (r.email === recipient.email ||
              r.phonenumber === recipient.phonenumber))) &&
        r.recipientType === recipients.openStateType
    )

  const _checkListedRecipients = (recipient: IRecipientModel) =>
    listedRecipients.some((r) => r.nameId === recipient.nameId)

  const handleSingleCheckboxChange = (recipient: IRecipientModel) => () => {
    const isInInitialRecievers = _checkInitialRecipients(recipient)
    const isInRemovalQueue = _checkListedRemovalQueue(recipient)

    if (isInInitialRecievers) {
      if (isInRemovalQueue) {
        dispatch(removeRecipientFromRemovalQueue(recipient.nameId))
        return
      }

      dispatch(addRecipientToRemovalQueue(recipient))
      return
    }

    const isListedInQueue = _checkListedRecipients(recipient)
    if (isListedInQueue) {
      dispatch(removeRecipient(recipient.nameId))
      return
    }

    dispatch(addRecipient(recipient))
  }

  const recipientChecked = (recipient: IRecipientModel) => {
    const isInRemovalQueue = _checkListedRemovalQueue(recipient)
    if (isInRemovalQueue) return false

    const isInSendMessage = _checkInitialRecipients(recipient)
    if (isInSendMessage) return true

    const isListedInQueue = _checkListedRecipients(recipient)
    if (isListedInQueue) return true

    return false
  }

  const recipientDisabled = (recipient: IRecipientModel) => {
    return Boolean(
      state && blockNotLoggedInUsersForState[state] && !recipient.hasSignedIn
    )
  }

  const handleMultiCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked
    const recipientsList = recipients?.data?.result || []

    if (recipientsList.length === 0) return

    /**
     * Add recipients logic
     */
    if (checked) {
      // Recipients that should be removed from the removal queue
      const recipientsToRemoveFromRemovalQueue = recipientsList.filter((r) =>
        _checkListedRemovalQueue(r)
      )
      if (recipientsToRemoveFromRemovalQueue.length) {
        dispatch(
          removeRecipientsFromRemovalQueue(recipientsToRemoveFromRemovalQueue)
        )
      }

      let recipientsToAddToListedRecipients = recipientsList.filter(
        (r) => !_checkListedRemovalQueue(r)
      )

      recipientsToAddToListedRecipients =
        recipientsToAddToListedRecipients.filter((r) => !recipientDisabled(r))

      if (recipientsToAddToListedRecipients.length) {
        dispatch(addAllRecipients(recipientsToAddToListedRecipients))
      }
      return
    }

    /**
     * Remove recipients logic
     */
    // Add to removal queue when a user is unchecking the "check all" checkbox,
    // and recipients that is was added to email is in the list
    const recipientsToRemoveFromSendMessage = recipientsList.filter((r) =>
      _checkInitialRecipients(r)
    )

    if (recipientsToRemoveFromSendMessage.length) {
      dispatch(addRecipientsToRemovalQueue(recipientsToRemoveFromSendMessage))
    }

    dispatch(removeAllRecipients())
  }

  const allRecipientsChecked = () =>
    recipients?.data?.result.every(
      (rec) => recipientChecked(rec) || recipientDisabled(rec)
    )

  return {
    recipientChecked,
    handleSingleCheckboxChange,
    allRecipientsChecked,
    handleMultiCheckboxChange,
    recipientDisabled,
  }
}

export default useRecipientsChecked
